import { useState } from "react"
import { pmndpAPI } from "../../../services/pmndp.api";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import PMNDPModal from './PMNDPModal';
import { PMNDPFormField, PMNDPFormSection, PMNDPFormButtons } from './PMNDPForm';
import styles from './pmndp-enhanced.module.css';

const StartDialysis = ({ onClose, patient_name, patient_id, dialysis_id, abha_number, abha_address, age, gender, mobile, getDiaList }) => {

    const labels = {
        startDialysis: "Start Dialysis",
        close: "Close",
        personalDetails: "Personal Details",
        abhaNumber: "ABHA Number/Health ID",
        abhaAddress: "ABHA Address",
        patientName: "Patient Name",
        age: "Age",
        gender: "Gender",
        select: "Select",
        male: "Male",
        female: "Female",
        transgender: "Transgender",
        healthRecordDetails: "Health Record Details",
        bloodPressure: "Blood Pressure",
        weight: "Weight",
        hemoglobin: "Hemoglobin",
        contactDetails: "Contact Details",
        mobileNo: "Mobile No.",
        selectMode: "Select Mode:",
        selectModeOption: "Select Mode",
        otp: "OTP",
        biometric: "Biometric",
        enterOtp: "Enter OTP",
        verifyPatientIdentity: "Verify Patient Identity",
        clickHere: "Click Here",
        cancel: "Cancel",
        verify: "Verify",
        errorUpdatingField: "Error updating form field",
        selectValidMode: "Please select a valid mode",
        patientIdRequired: "Patient ID is required to send OTP",
        otpSentSuccessfully: "OTP sent successfully.",
        failedToSendOtp: "Failed to send OTP.",
        errorSendingOtp: "Error sending OTP.",
        otpVerifiedSuccessfully: "OTP verified successfully.",
        errorAfterVerification: "Error occurred after verification",
        failedToVerifyOtp: "Failed to verify OTP.",
        errorVerifyingOtp: "Error verifying OTP."
    };

    const [isPageLoading, setIsPageLoading] = useState(false);

    const [form, setForm] = useState({
        otp: '',
        otp_code: '',
        patient_id: patient_id || '',
        pre_blood_pressure: '',
        pre_weight: '',
        hemoglobin: '',
        mobile: mobile || '',
        abha_number: abha_number || '',
        abha_address: abha_address || '',
        patient_name: patient_name || '',
        age: age || '',
        gender: gender || '',
        mode: 'Mobile',
    });

    const handleChange = (e) => {
        try {
            const { name, value } = e.target;
            if (!name) {
                console.error('Input field missing name attribute');
                return;
            }
            setForm(prev => ({
                ...prev,
                [name]: value
            }));
        } catch (error) {
            console.error('Error handling form change:', error);
            toast.error(labels.errorUpdatingField);
        }
    };

    const handleModeChange = async (e) => {
        try {
            const value = e.target.value;
            if (!value) {
                toast.error(labels.selectValidMode);
                return;
            }
            
            setForm(prev => ({
                ...prev,
                mode: value
            }));
            
            if (value === "OTP") {
                if (!patient_id) {
                    toast.error(labels.patientIdRequired);
                    return;
                }
                
                setIsPageLoading(true);
                const resp = await pmndpAPI.sendDialysisOtp({ patient_id: patient_id });
                if (resp && resp.success) {
                    toast.success(labels.otpSentSuccessfully);
                } else {
                    const errorMsg = resp?.msg || resp?.message || labels.failedToSendOtp;
                    toast.error(errorMsg, { className: "custom-toast-error" });
                }
            }
        } catch (err) {
            console.error('Error in mode change:', err);
            const errorMsg = err?.response?.data?.message || err?.message || labels.errorSendingOtp;
            toast.error(errorMsg, { className: "custom-toast-error" });
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            otp: form.otp,
            otp_code: 'verify_health_id',
            patient_id: patient_id,
            dialysis_id: dialysis_id,
            pre_blood_pressure: form.pre_blood_pressure,
            pre_weight: form.pre_weight,
            hemoglobin: form.hemoglobin
        };

        try {
            setIsPageLoading(true);
            const resp = await pmndpAPI.verifyPatientOtp(payload);
            if (resp && resp.success) {
                setIsPageLoading(false);
                toast.success(labels.otpVerifiedSuccessfully);

                setTimeout(() => {
                    try {
                        if (typeof onClose === 'function') onClose();
                        if (typeof getDiaList === 'function') getDiaList();
                    } catch (callbackError) {
                        console.error('Error in callback functions:', callbackError);
                        toast.error(labels.errorAfterVerification);
                    }
                }, 1200);

            } else {
                toast.error(resp?.msg || labels.failedToVerifyOtp, { className: "custom-toast-error" });
                setIsPageLoading(false);
            }
        } catch (err) {
            console.error('Error verifying OTP:', err);
            toast.error(labels.errorVerifyingOtp, { className: "custom-toast-error" });
            setIsPageLoading(false);
        }
    };

    const modeOptions = [
        { value: 'OTP', label: labels.otp },
        { value: 'Biometric', label: labels.biometric }
    ];

    return (
        <>
            
            {isPageLoading && <Loader />}

            <PMNDPModal
                isOpen={true}
                onClose={onClose}
                title={labels.startDialysis}
                icon="fa-play-circle"
                size="xlarge"
                footer={
                    <PMNDPFormButtons
                        onSave={handleSubmit}
                        onCancel={onClose}
                        saveText={labels.verify}
                        cancelText={labels.cancel}
                        isLoading={isPageLoading}
                    />
                }
            >
                <form onSubmit={handleSubmit}>
                    <PMNDPFormSection title={labels.personalDetails} icon="fa-user">
                        <PMNDPFormField
                            label={labels.abhaNumber}
                            value={form.abha_number}
                            disabled
                            placeholder="ABHA Number"
                        />
                        
                        <PMNDPFormField
                            label={labels.abhaAddress}
                            value={form.abha_address}
                            disabled
                            placeholder="ABHA Address"
                        />
                        
                        <PMNDPFormField
                            label={labels.patientName}
                            value={form.patient_name}
                            disabled
                            placeholder="Patient Name"
                        />
                        
                        <PMNDPFormField
                            label={labels.age}
                            type="number"
                            value={form.age}
                            disabled
                            placeholder="Age"
                        />
                        
                        <PMNDPFormField
                            label={labels.gender}
                            type="select"
                            value={form.gender}
                            disabled
                            options={[
                                { value: 'Male', label: labels.male },
                                { value: 'Female', label: labels.female },
                                { value: 'Transgender', label: labels.transgender }
                            ]}
                        />
                    </PMNDPFormSection>

                    <PMNDPFormSection title={labels.healthRecordDetails} icon="fa-heartbeat">
                        <PMNDPFormField
                            label={labels.bloodPressure}
                            value={form.pre_blood_pressure}
                            onChange={(e) => {
                                let value = e.target.value.replace(/[^\d]/g, '');
                                if (value.length > 3) {
                                    value = value.slice(0, 3) + '/' + value.slice(3, 5);
                                }
                                setForm({ ...form, pre_blood_pressure: value });
                            }}
                            maxLength={6}
                            placeholder="120/80"
                        />
                        
                        <PMNDPFormField
                            label={labels.weight}
                            name="pre_weight"
                            value={form.pre_weight}
                            onChange={handleChange}
                            placeholder="Weight (kg)"
                        />
                        
                        <PMNDPFormField
                            label={labels.hemoglobin}
                            name="heomoglobin"
                            value={form.heomoglobin}
                            onChange={handleChange}
                            placeholder="Hemoglobin (g/dL)"
                        />
                    </PMNDPFormSection>

                    <PMNDPFormSection title="Verification & Contact" icon="fa fa-address-book-o">
                        <PMNDPFormField
                            label={labels.mobileNo}
                            value={form.mobile}
                            disabled
                            placeholder="Mobile Number"
                        />
                        
                        <PMNDPFormField
                            label={labels.selectMode}
                            type="select"
                            value={form.mode}
                            onChange={handleModeChange}
                            options={modeOptions}
                            placeholder="Select verification mode"
                            required
                        />
                        
                        {form.mode === "OTP" && (
                            <PMNDPFormField
                                label={labels.enterOtp}
                                name="otp"
                                value={form.otp}
                                onChange={handleChange}
                                placeholder="Enter 6-digit OTP"
                                maxLength={6}
                                required
                            />
                        )}
                        
                        {form.mode === "Biometric" && (
                            <div style={{
                                gridColumn: '1 / -1',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: '30px',
                                background: '#f8fafc',
                                borderRadius: '12px',
                                border: '2px dashed #e2e8f0',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    width: '80px',
                                    height: '80px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '20px'
                                }}>
                                    <i className="fa fa-fingerprint" style={{ fontSize: '32px', color: 'white' }}></i>
                                </div>
                                <h3 style={{ margin: '0 0 10px 0', color: '#374151' }}>{labels.verifyPatientIdentity}</h3>
                                <p style={{ margin: '0 0 20px 0', color: '#6b7280' }}>Place finger on biometric scanner</p>
                                <button
                                    type="button"
                                    className="pmndp-btn pmndp-btn-primary"
                                    onClick={() => {
                                        // Add biometric trigger logic here
                                    }}
                                >
                                    <i className="fa fa-hand-paper"></i>
                                    {labels.clickHere}
                                </button>
                            </div>
                        )}
                    </PMNDPFormSection>
                </form>
            </PMNDPModal>
        </>
    )
}

export default StartDialysis
